import type { AnalyticsEventName, AnalyticsEventProperties } from './analyticsTypes'

type EventAllowedProperties = {
  [EventName in AnalyticsEventName]: readonly (keyof AnalyticsEventProperties[EventName])[]
}

export const analyticsEventAllowedProperties: EventAllowedProperties = {
  user_signed_in: ['organization_id', 'user_role', 'user_profile', 'regional_unit', 'email_domain'],
  user_signed_out: [
    'organization_id',
    'user_role',
    'user_profile',
    'regional_unit',
    'email_domain',
  ],
  screen_viewed: ['screen', 'source_screen'],
  feature_used: ['feature', 'source_screen', 'screen', 'filters_count', 'result_count'],
  export_started: ['export_type', 'source_screen', 'feature', 'result_count'],
  export_created: ['export_type', 'source_screen', 'feature', 'result_count'],
  export_failed: ['export_type', 'source_screen', 'feature', 'error_type'],

  sentinel_opened: ['source_screen'],
  sentinel_query_started: [
    'query_type',
    'time_range_type',
    'geographic_level',
    'prediction_enabled',
    'filters_count',
    'layers_count',
  ],
  sentinel_what_selected: ['query_type', 'feature', 'filters_count', 'layers_count'],
  sentinel_when_selected: ['query_type', 'time_range_type'],
  sentinel_where_selected: [
    'geographic_level',
    'previous_geographic_level',
    'geographic_code',
    'geographic_label_type',
  ],
  sentinel_query_submitted: [
    'query_type',
    'time_range_type',
    'geographic_level',
    'prediction_enabled',
    'filters_count',
    'layers_count',
  ],
  sentinel_result_loaded: [
    'query_type',
    'time_range_type',
    'geographic_level',
    'prediction_enabled',
    'filters_count',
    'layers_count',
    'result_count',
    'render_time_ms',
  ],
  sentinel_query_failed: ['query_type', 'time_range_type', 'geographic_level', 'error_type'],
  sentinel_prediction_enabled: ['query_type', 'time_range_type', 'geographic_level'],
  sentinel_filter_changed: ['query_type', 'filters_count', 'geographic_level', 'time_range_type'],

  map_type_changed: ['map_type', 'source_screen'],
  map_layer_enabled: ['layer_type', 'layer_identifier', 'layers_count', 'source_screen'],
  map_layer_disabled: ['layer_type', 'layer_identifier', 'layers_count', 'source_screen'],
  map_region_selected: [
    'geographic_level',
    'previous_geographic_level',
    'geographic_code',
    'geographic_label_type',
  ],
  map_drilldown_performed: ['geographic_level', 'previous_geographic_level', 'source_screen'],
  map_exported: ['export_type', 'map_type', 'source_screen'],
  map_exploration_summary: [
    'total_map_movements',
    'maximum_zoom_level',
    'minimum_geographic_level_reached',
    'regions_selected_count',
    'layers_used_count',
    'map_types_used',
    'session_duration_seconds',
    'source_screen',
  ],

  study_list_opened: ['source_screen'],
  study_opened: ['study_identifier', 'source_screen'],
  study_downloaded: ['study_identifier', 'source_screen'],
  study_shared: ['study_identifier', 'source_screen'],

  insight_list_opened: ['source_screen'],
  insight_opened: ['insight_identifier', 'source_screen'],
  insight_saved: ['insight_identifier', 'source_screen'],
  insight_shared: ['insight_identifier', 'source_screen'],
  insight_dismissed: ['insight_identifier', 'source_screen'],

  ai_panel_opened: ['source_screen'],
  ai_prompt_submitted: ['ai_interaction_category', 'source_screen', 'feature'],
  ai_response_received: [
    'ai_interaction_category',
    'source_screen',
    'render_time_ms',
    'ai_model_key',
    'ai_input_tokens',
    'ai_output_tokens',
    'ai_total_tokens',
    'ai_cached_input_tokens',
    'ai_estimated_cost_usd',
    'ai_finish_reason',
    'ai_tool_calls_count',
  ],
  ai_response_copied: ['ai_interaction_category', 'source_screen'],
  ai_response_saved: ['ai_interaction_category', 'source_screen'],
  ai_response_rated: ['ai_interaction_category', 'source_screen'],
}

export const analyticsEventNames = Object.keys(
  analyticsEventAllowedProperties,
) as AnalyticsEventName[]
